/** The Business Opportunities toolbar: view, search, filters, view-specific controls and actions. */

import type { Dispatch, SetStateAction } from 'react';
import { ChartScatter, CircleHelp, Columns3, Lightbulb, List, Search } from 'lucide-react';

import type { Disposition, IdeaCatalogue, IdeaList, VerdictOutcome } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { MultiSelect } from '@/ui/MultiSelect';
import { RefreshButton } from '@/ui/RefreshButton';
import { ViewToggle, type ViewOption } from '@/ui/ViewToggle';

import { type IdeaFilters, type IdeaView } from './ideaFilters';
import { DISPOSITION_LABEL, OUTCOMES, OUTCOME_STYLE } from './ideaUi';
import { industryOptions } from './useBusinessOpportunities';

const DISPOSITIONS: Disposition[] = ['active', 'parked', 'archived'];

const VIEWS: ViewOption<IdeaView>[] = [
  { key: 'matrix', label: 'Matrix', Icon: ChartScatter },
  { key: 'list', label: 'List', Icon: List },
];

function IdeaFilterSelects({
  filters,
  counts,
  outcomeCounts,
  industries,
}: {
  filters: IdeaFilters;
  counts: IdeaList['counts'];
  outcomeCounts: IdeaList['verdict_counts'];
  industries: IdeaCatalogue['industries'];
}) {
  const { params, refine } = filters;
  return (
    <>
      <MultiSelect
        label="Filter by status"
        allLabel="All statuses"
        noun="statuses"
        options={DISPOSITIONS.map((d) => ({ value: d, label: DISPOSITION_LABEL[d], meta: counts[d] }))}
        value={params.dispositions}
        onChange={(value) => refine({ dispositions: value as Disposition[] })}
      />
      <MultiSelect
        label="Filter by verdict"
        allLabel="All verdicts"
        noun="verdicts"
        options={OUTCOMES.map((o) => ({ value: o, label: OUTCOME_STYLE[o].label, meta: outcomeCounts[o] }))}
        value={params.outcomes}
        onChange={(value) => refine({ outcomes: value as VerdictOutcome[] })}
      />
      <MultiSelect
        label="Filter by industry"
        allLabel="All industries"
        noun="industries"
        options={industryOptions(industries)}
        value={params.industries}
        onChange={(value) => refine({ industries: value })}
      />
    </>
  );
}

function DimensionsToggle({ showDimensions, setShowDimensions }: { showDimensions: boolean; setShowDimensions: Dispatch<SetStateAction<boolean>> }) {
  return (
    <button
      type="button"
      className="btn btn-secondary btn-icon dims-toggle"
      aria-label="Show dimension columns"
      title={showDimensions ? 'Hide dimension columns' : 'Show dimension columns'}
      aria-pressed={showDimensions}
      onClick={() => setShowDimensions((v) => !v)}
    >
      <Columns3 aria-hidden="true" />
    </button>
  );
}

function ColourByControl({ colorBy, setColorBy }: Pick<IdeaFilters, 'colorBy' | 'setColorBy'>) {
  return (
    <>
      <span className="filter-divider" aria-hidden="true" />
      <div className="opp-controls">
        <span className="opp-controls-label" id="opp-colour">Colour by</span>
        <div className="segmented" role="radiogroup" aria-labelledby="opp-colour">
          <button type="button" role="radio" aria-checked={colorBy === 'verdict'} onClick={() => setColorBy('verdict')}>
            Verdict
          </button>
          <button type="button" role="radio" aria-checked={colorBy === 'industry'} onClick={() => setColorBy('industry')}>
            Industry
          </button>
        </div>
      </div>
      <span className="filter-divider" aria-hidden="true" />
    </>
  );
}

export function IdeasToolbar({
  filters,
  counts,
  outcomeCounts,
  industries,
  showDimensions,
  setShowDimensions,
  onSubmitIdea,
  onShowGuide,
}: {
  filters: IdeaFilters;
  counts: IdeaList['counts'];
  outcomeCounts: IdeaList['verdict_counts'];
  industries: IdeaCatalogue['industries'];
  showDimensions: boolean;
  setShowDimensions: Dispatch<SetStateAction<boolean>>;
  onSubmitIdea: () => void;
  onShowGuide: () => void;
}) {
  const { can } = useSession();
  const { view } = filters;
  return (
    <div className="toolbar filter-panel">
      <ViewToggle options={VIEWS} value={view} onChange={filters.setView} label="View" />
      <span className="filter-divider" aria-hidden="true" />
      <div className="search-field">
        <Search aria-hidden="true" />
        <input
          type="search"
          value={filters.search}
          placeholder="Search ideas"
          aria-label="Search titles and descriptions"
          onChange={(e) => filters.refine({ q: e.target.value })}
        />
      </div>
      <IdeaFilterSelects filters={filters} counts={counts} outcomeCounts={outcomeCounts} industries={industries} />
      <span className="filter-divider dims-divider" aria-hidden="true" />
      {view === 'list' && <DimensionsToggle showDimensions={showDimensions} setShowDimensions={setShowDimensions} />}
      {view === 'matrix' && <ColourByControl colorBy={filters.colorBy} setColorBy={filters.setColorBy} />}
      <RefreshButton iconOnly queryKeys={[['ideas']]} />
      {can('idea.submit') && (
        <button type="button" className="btn btn-primary" onClick={onSubmitIdea}>
          <Lightbulb aria-hidden="true" /> Submit an idea
        </button>
      )}
      <button
        type="button"
        className="btn btn-secondary btn-icon"
        aria-label="How ideas are scored"
        title="How ideas are scored"
        onClick={onShowGuide}
      >
        <CircleHelp aria-hidden="true" />
      </button>
    </div>
  );
}
